/**
 * ═══════════════════════════════════════════════════════════════
 *  EXECUTION DOMAIN — UX/IA layer (single-wave build)
 * ═══════════════════════════════════════════════════════════════
 *
 * "Execution" is a first-class domain in the navigation, NOT a new
 * truth layer. Every page under /execution/* reads through canonical
 * resolvers and renders read-only.
 *
 * HARD RULES (enforced by tests in src/test/executionDomain*.test.ts):
 *  1. No new canonical entities — pages compose existing data only.
 *  2. No new writers — only `acknowledgeFriction()` (Phase 2a sink)
 *     is permitted from any /execution surface.
 *  3. RBAC: super_admin, admin, ops_manager only.
 *  4. No `contractValue` / `payoutAmount` / PII in any page text.
 *  5. No PM-attributed analytics until canonical PM identity lands.
 *  6. Cockpit primitives (`CockpitPage`, etc.) for analytical surfaces.
 *  7. `MIN_SAMPLE_FOR_DISPLAY = 3` enforced for any rate/percentage.
 */

import type { UserRole } from "./RoleContext";

export const EXECUTION_DOMAIN_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  "super_admin",
  "admin",
  "ops_manager",
  "it_staff",
]);

export function canAccessExecutionDomain(role: UserRole): boolean {
  return EXECUTION_DOMAIN_ROLES.has(role);
}

/**
 * Per-route operational exception.
 * `head_of_installation` is granted access to ONLY `/execution/installs-live`
 * and `/execution/install-today` — never any other /execution/* route. The
 * rest of the domain (governance, holdbacks, vetting, promotion queue, etc.)
 * remains restricted to EXECUTION_DOMAIN_ROLES.
 */
export const EXECUTION_INSTALLS_LIVE_ROUTE = "/execution/installs-live" as const;
export const EXECUTION_INSTALL_TODAY_ROUTE = "/execution/install-today" as const;

export const EXECUTION_INSTALLS_LIVE_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  "super_admin",
  "admin",
  "ops_manager",
  "head_of_installation",
  "it_staff",
]);

export function canAccessInstallsLive(role: UserRole): boolean {
  return EXECUTION_INSTALLS_LIVE_ROLES.has(role);
}

/** Canonical list of /execution/* routes. Source of truth for the sidebar group. */
export const EXECUTION_DOMAIN_ROUTES: readonly string[] = [
  "/execution",
  "/execution/control-tower",
  "/execution/timeline",
  "/execution/installer-network",
  "/execution/vetting/:contractorId",
  "/execution/qa-callbacks",
  "/execution/holdbacks",
  "/execution/friction",
  "/execution/promotion-queue",
  "/execution/summary",
  "/execution/governance",
  EXECUTION_INSTALLS_LIVE_ROUTE,
  EXECUTION_INSTALL_TODAY_ROUTE,
] as const;

/**
 * Whitelist of writer function names that /execution/* pages may invoke.
 * Anything outside this set is a guard-test failure.
 */
export const EXECUTION_DOMAIN_WRITER_WHITELIST: ReadonlySet<string> = new Set<string>([
  "acknowledgeFriction",
]);
